import React, { useEffect, useState } from 'react';
import { Spin, Tooltip } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';

import ProductImg from '../../../../../assets/images/newImages/nestle.svg';

import AgGrid from '../../../../../components/ag-grid-table/index';
import PlacementCard from '../../../../../components/uiComponent/placement-card';
import ShipmentLocationsPanel, { ShipmentsLayout } from '../../../../../components/uiComponent/shipment-locations-panel';
import WorkOrderPageHeader from '../../../../../components/uiComponent/work-order-page-header';
import Button from '../../../../../components/uiComponent/button/index';
import useResponsiveTableHeight from '../../../../../hooks/useResponsiveTableHeight';

import { ConfirmPlacementOption, CancelInboundPlan, GetPlacementOptions, GetShipmentItems, updateState } from '../../../../../slices/shipment-plan';
import { GetWorkOrderDetails } from '../../../../../slices/work-orders';

import { PlacementOptionWrapper } from './style';

const defaultColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  suppressRowClickSelection: true,
  suppressMenu: true,
  suppressMovable: true
};

const PlacementOption = () => {
  const { workOrderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [inboundPlanDataSet, setInboundPlanDataSet] = useState({ placementOptions: [], shipments: [], items: [] });
  const [selectedOptions, setSelectedOptions] = useState({ placementIndex: 0, shipmentIndex: 0 });
  const { tableHeight, tableSectionRef, paginationRef } = useResponsiveTableHeight({
    minHeight: 220,
    bottomSpacing: 24,
    observeSelectors: ['.stats-footer']
  });

  const {
    workOrderDetails
  } = useSelector((state) => state.workOrders || {});
  const { currentUser } = useSelector((state) => state.auth || {});

  const {
    inboundPlans = [],
    confirmingPlacementOption,
    cancelingInboundPlan,
    fetchingPlacementOptions,
    fetchingShipmentItems
  } = useSelector((state) => state.shipmentPlan || {});

  useEffect(() => {

    dispatch(GetWorkOrderDetails(workOrderId)).then(({ payload }) => {

      // if work order is not in draft state, redirect to confirm placement or work order page
      if (payload?.data?.status !== 'Draft') {
        if(currentUser?.role?.id === 5){
          navigate('/work-orders', {replace: true});
        }
        else{
          navigate(`/confirm-placement/${workOrderId}`);
        }
        return;
      }
      // clearing placement options states
      setInboundPlanDataSet({ placementOptions: [], shipments: [], items: [] });
      setSelectedOptions({ placementIndex: 0, shipmentIndex: 0 });
      dispatch(GetPlacementOptions(workOrderId))
        .then(({ payload }) => {
          const { placementOptions } = payload || {};

          const mappedPlacementOptions = placementOptions?.map((placementOption, index) => {
            const { discounts, expiration, fees, placementOptionId } = placementOption || {};

            return {
              id: placementOptionId,
              placementOption: `Placement Option ${index + 1}`,
              shipments: 0,
              discount: discounts?.reduce((total, fee) => total + (fee?.value?.amount || 0), 0) || 0,
              expiry: expiration ? dayjs(expiration).format('M/D/YY') : '',
              placementFee: fees?.[0]?.value?.amount || 0
            };
          }) || [];

          setInboundPlanDataSet({
            placementOptions: mappedPlacementOptions
          });

          if (!isEmpty(placementOptions)) dispatch(GetShipmentItems({ placementOptions, workOrderId }));
        });
    });

    return () => {
      setInboundPlanDataSet({ placementOptions: [], shipments: [], items: [] });
      setSelectedOptions({ placementIndex: 0, shipmentIndex: 0 });
      dispatch(updateState({ key: 'inboundPlans', value: [] }));
      dispatch(updateState({ key: 'placementOptions', value: [] }));
    };
  }, []);

  useEffect(() => {
    if (!isEmpty(inboundPlans)) {
      const placementOptions = inboundPlans?.map((plan, index) => {
        const { placementOption, shipmentDetails, items } = plan || {};
        const { discounts, expiration, fees, placementOptionId } = placementOption || {};

        return {
          id: placementOptionId,
          placementOption: `Placement Option ${index + 1}`,
          shipments: shipmentDetails?.length || 0,
          units: items?.reduce((sum, item) => sum + (item?.quantity || 0), 0) || 0,
          discount: discounts?.length || 0,
          expiry: expiration ? dayjs(expiration).format('M/D/YY') : '',
          placementFee: fees?.[0]?.value?.amount || 0
        };
      });

      setInboundPlanDataSet({
        placementOptions
      });

      selectPlacementOption(0);
    }
  }, [inboundPlans]);

  const columnDefs = [
    {
      headerName: 'Items',
      flex: 1,
      minWidth: 200,
      cellRenderer: ({ data }) => {
        const { image_url, title } = data || {};

        return (
          <div className='product-item-wrapper'>
            <img
              src={image_url || ProductImg}
              alt="Product"
            />
            <span className='product-item'>{title || '---'}</span>
          </div>
        );
      }
    },
    {
      headerName: 'SKU',
      field: 'msku',
      flex: 1,
      minWidth: 100
    },
    {
      headerName: 'Quantity',
      field: 'quantity',
      flex: 1,
      minWidth: 100
    }
  ];

  const selectPlacementOption = (index) => {
    const shipments = inboundPlans[index]?.shipmentDetails || [];
    const shipmentIndex = 0;
    const shipmentId = shipments[shipmentIndex]?.shipmentId;
    const items = inboundPlans[index]?.items?.filter((item) => item.shipmentId === shipmentId) || [];

    setSelectedOptions({ placementIndex: index, shipmentIndex });
    setInboundPlanDataSet(inboundPlanDataSet => ({ ...inboundPlanDataSet, shipments, items }));
  };

  const selectShipment = (shipmentIndex) => {
    const shipmentId = inboundPlanDataSet.shipments[shipmentIndex].shipmentId;
    const items = inboundPlans[selectedOptions.placementIndex]?.items?.filter((item) => item.shipmentId === shipmentId) || {};

    setSelectedOptions(selectedOptions => ({ ...selectedOptions, shipmentIndex }));
    setInboundPlanDataSet(inboundPlanDataSet => ({ ...inboundPlanDataSet, items }));
  };

  const handleCancelInboundPlan = () => {
    dispatch(CancelInboundPlan({
      workOrderId
    }))
      .then(({ payload }) => {
        if (payload?.success) navigate(`/inventory/work-order/${workOrderId}`);
      });
  };


  const handleConfirmPlacementOption = () => {
    dispatch(ConfirmPlacementOption({
      workOrderId,
      placementOptionId: placementOptions[selectedOptions?.placementIndex]?.id
    }))
      .then(({ payload }) => {
        if (payload?.success) {
          if(currentUser?.role?.id === 5){
            navigate(`/shipping/${workOrderId}`);
          }else{
            navigate(`/confirm-placement/${workOrderId}`);
          }
        }
      });
  };

  const { placementOptions = [], shipments = [], items = [] } = inboundPlanDataSet || {};
  const loading = fetchingPlacementOptions || confirmingPlacementOption || cancelingInboundPlan || fetchingShipmentItems;
  const placementOptionsLoading = fetchingPlacementOptions || confirmingPlacementOption || cancelingInboundPlan;

  const selectedOption = placementOptions[selectedOptions?.placementIndex];
  // Every unit in this option, across all its shipments — the shared denominator for each
  // shipment card's "share of this option" progress bar.
  const selectedOptionUnits = inboundPlans[selectedOptions.placementIndex]?.items
    ?.reduce((sum, item) => sum + (item?.quantity || 0), 0) || 0;

  // Weighted average miles/unit across the selected option's shipments — only shown when every
  // shipment in it has a real mileage figure, otherwise it's shown as unknown rather than guessed.
  let avgMilesPerUnit = null;
  if (selectedOptionUnits > 0) {
    let weighted = 0;
    let allKnown = true;
    shipments.forEach((shipment) => {
      const shipmentUnits = inboundPlans[selectedOptions.placementIndex]?.items
        ?.filter(({ shipmentId }) => shipment.shipmentId === shipmentId)
        ?.reduce((sum, item) => sum + (item?.quantity || 0), 0) || 0;
      if (shipment?.miles == null) allKnown = false;
      else weighted += shipment.miles * shipmentUnits;
    });
    if (allKnown) avgMilesPerUnit = Math.round(weighted / selectedOptionUnits);
  }

  const selectedShipment = shipments[selectedOptions.shipmentIndex];
  const selectedShipmentUnits = items.reduce((sum, item) => sum + (item?.quantity || 0), 0);
  const selectedShipmentSkuCount = new Set(items.map((item) => item.msku)).size;

  return (
    <PlacementOptionWrapper>
      <WorkOrderPageHeader
        orderName={workOrderDetails?.order_name}
        title="Choose a placement option"
        subtitle="Amazon offers these ways to split your inbound. Pick one to see where each unit goes."
        expiryLabel={selectedOption?.expiry ? `This option expires ${selectedOption.expiry}` : null}
      />
      <div className={`options-row ${placementOptionsLoading ? 'loading-accured' : ''}`}>
        <Spin wrapperClassName="placement-cards-loading" tip='Loading...' spinning={placementOptionsLoading}>
          <PlacementCard
            cardsData={placementOptions}
            activePlacementIndex={selectedOptions?.placementIndex}
            selectPlacementOption={selectPlacementOption}
          />
        </Spin>
      </div>
      <div className='shipments-main-row'>
      <Spin tip='Loading...' wrapperClassName="placement-cards-loading" spinning={loading}>
        <ShipmentsLayout>
          {!isEmpty(shipments) && (
            <ShipmentLocationsPanel
              title="Destinations"
              hint="Units · distance"
              rowsMaxHeight={tableHeight}
              rows={shipments.map((item, index) => {
                const shipmentItems = inboundPlans[selectedOptions.placementIndex]?.items?.filter(({ shipmentId }) => item.shipmentId === shipmentId);
                const shipmentUnits = shipmentItems?.reduce((sum, i) => sum + (i?.quantity || 0), 0) || 0;
                const skuCount = new Set(shipmentItems?.map((i) => i.msku)).size;
                return {
                  key: item.shipmentId,
                  active: selectedOptions?.shipmentIndex === index,
                  onClick: () => selectShipment(index),
                  id: item?.fcId || item?.destinationCenterId || '---',
                  subLabel: `${skuCount} SKU${skuCount === 1 ? '' : 's'}`,
                  units: shipmentUnits,
                  milesLabel: item?.miles != null ? `${item.miles.toLocaleString('en-US')} mi` : '— mi'
                };
              })}
            />
          )}
          <div className='items-card'>
            <div className='items-card-header'>
              <h2>Shipment {shipments.length ? selectedOptions.shipmentIndex + 1 : 0} of {shipments.length} <span>&rarr; {selectedShipment?.fcId || selectedShipment?.destinationCenterId || '---'}</span></h2>
              <span className='items-card-sub'>{selectedShipmentSkuCount} SKU{selectedShipmentSkuCount === 1 ? '' : 's'} &middot; {selectedShipmentUnits} units &middot; {selectedShipment?.miles != null ? `${selectedShipment.miles.toLocaleString('en-US')} mi` : '— mi'}</span>
            </div>
            <div ref={tableSectionRef} className='items-table-section'>
              <AgGrid
                user_referrals={items}
                defaultColDef={defaultColDef}
                columnDefinitions={columnDefs}
                rowHeight={64}
                headerHeight={36}
                containerHeight={tableHeight}
                masterDetail={true}
                detailRowAutoHeight={true}
                getRowNodeId={({ msku }) => (msku)}
                className="sku-table"
              />
            </div>
          </div>
        </ShipmentsLayout>
      </Spin>
      </div>
      <div ref={paginationRef}>
        <div className='stats-footer'>
          <dl>
            <div className='stat'>
              <dt>Selected</dt>
              <dd>{selectedOption?.placementOption || '---'}</dd>
            </div>
            <div className='stat'>
              <dt>Shipments</dt>
              <dd>{selectedOption?.shipments ?? '---'}</dd>
            </div>
            <div className='stat'>
              <dt>Units</dt>
              <dd>{selectedOptionUnits}</dd>
            </div>
            <div className='stat'>
              <dt>Avg distance per unit</dt>
              <dd>{avgMilesPerUnit != null ? `${avgMilesPerUnit.toLocaleString('en-US')} mi` : '---'}</dd>
            </div>
            <div className='stat'>
              <dt>Placement fee</dt>
              <dd>${selectedOption?.placementFee ?? 0}</dd>
            </div>
          </dl>
          <footer>
            <Button
              text="Cancel Plan"
              className="danger mb-0"
              minWidth="90px"
              onClick={handleCancelInboundPlan}
              disabled={loading}
            />
            <Tooltip placement='top'>
              <span>
                <Button
                  text="Confirm Placement Option"
                  className="mb-0"
                  minWidth="90px"
                  onClick={handleConfirmPlacementOption}
                  disabled={loading || ['Confirmed', 'In Progress', 'Done'].includes(workOrderDetails?.status) || !placementOptions[selectedOptions?.placementIndex]?.id}
                />
              </span>
            </Tooltip>
          </footer>
        </div>
      </div>
    </PlacementOptionWrapper>
  );
};

export default PlacementOption;
